import { useMemo } from 'react';

import { Menubar } from 'primereact/menubar';
import { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import type { ShipmentSemen } from '@/services/shipping/types';

import { useShipmentContext } from '../../hooks/useShipmentContext';

const LOCKED_SHIPMENT_STATUSES = [
  ShipmentStatus.IN_PROGRESS as number,
  ShipmentStatus.SHIPPED as number,
];

type SemenMenubarProps = {
  selectedSemen: ShipmentSemen[];
  isBulkEditEnabled?: boolean;
  openBulkEditDialog?: () => void;
  executeRemoveFromShipmentWorkflow?: (semen: ShipmentSemen[]) => void;
};

export function SemenMenubar({
  selectedSemen,
  isBulkEditEnabled,
  openBulkEditDialog,
  executeRemoveFromShipmentWorkflow,
}: SemenMenubarProps) {
  const { t } = useTranslation();
  const { shipment } = useShipmentContext();
  const isRemoveSemenEnabled = useFeatureFlag(
    'Shipping_Add_Option_To_Remove_Semen_From_Shipment',
  );

  const shipmentStatus = shipment?.shipmentStatus;
  const isRemoveFromShipmentDisabled = shipmentStatus
    ? LOCKED_SHIPMENT_STATUSES.includes(shipmentStatus)
    : false;

  const menuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    if (isBulkEditEnabled && openBulkEditDialog) {
      items.push({
        label: 'Edit Semen',
        disabled: selectedSemen.length === 0,
        command() {
          openBulkEditDialog();
        },
      });
    }

    if (isRemoveSemenEnabled && executeRemoveFromShipmentWorkflow) {
      const disabled =
        selectedSemen.length === 0 || isRemoveFromShipmentDisabled;

      items.push({
        label: t('shipping.semenWorkflows.workflows'),
        icon: 'pi pi-wrench',
        disabled,
        items: [
          {
            label: t('shipping.semenWorkflows.removeFromShipment'),
            disabled,
            command() {
              executeRemoveFromShipmentWorkflow(selectedSemen);
            },
          },
        ],
      });
    }

    return items;
  }, [
    executeRemoveFromShipmentWorkflow,
    isBulkEditEnabled,
    isRemoveFromShipmentDisabled,
    isRemoveSemenEnabled,
    openBulkEditDialog,
    selectedSemen,
    t,
  ]);

  return <Menubar model={menuItems} />;
}
